import { isHighlyRated, isTopBrand } from '@noths/polaris-client-listings-api';
import type { CurrencyCode } from '@noths/polaris-client-localisation';
import { getProductImageURL } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import {
  toProductCardBadge,
  toProductCardDelivery,
  toProductCardRating,
} from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';

import type { ServiceRecommendedProduct } from 'src/builders/recommendedProduct';

/**
 * What a card needs beyond the product itself: the currency the request quoted its
 * prices in, and whether the rail is one partner's products, which decides whether
 * a badge about the partner says anything.
 */
export interface RecommendationsCardOptions {
  currency: CurrencyCode;
  isSinglePartnerRail: boolean;
  /**
   * The instant the arrival date is counted from, matching how the PLP grid's
   * `adaptProductToProductCardProps` takes it.
   *
   * Pass it. The default is a convenience for callers mapping a single product,
   * not the intended path: it is evaluated per call, so a caller mapping a
   * response and leaving it out dates each card from its own instant rather than
   * the rail from one. `useListingsRecommendations` captures it once per
   * response for that reason.
   */
  now?: Date;
  /**
   * Whether the site is presenting discounts as a sale rather than as an offer. Set
   * site-wide in Contentstack and read from `usePromoteAsSale` at the call site:
   * no discount in the catalogue distinguishes the two.
   */
  promoteAsSale: boolean;
}

/**
 * The fields the recommendations GA events report on, and nothing else.
 *
 * The carousel item the design system renders is a semantic `Product`, which
 * deliberately carries none of these, so tracking reads the service product
 * directly rather than the shape on screen. Narrow rather than the whole product
 * so the dependency is visible and a spec only has to build what it asserts on.
 */
export type RecommendationsTrackingProduct = Pick<
  ServiceRecommendedProduct,
  | 'code'
  | 'freeDomesticDelivery'
  | 'isNew'
  | 'onSale'
  | 'partner'
  | 'prices'
  | 'purchasable'
  | 'salePercentage'
  | 'title'
>;

/**
 * Adapts a listings-api recommendation into the OOUX `Product` the design system
 * consumes.
 *
 * The interface is deliberately semantic, so several service fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, `onSale` because the
 * discount is the pre-sale price being present, and `freeDomesticDelivery`
 * because the card expresses delivery as a cost and an arrival date, which this
 * endpoint cannot supply (see
 * `transformRecommendedProductToProductCarouselItem`). All of them still reach
 * GA, which reads the service product directly.
 *
 * The rating and the badge are not part of the product either. They are card
 * presentation, so they sit on the carousel item alongside it.
 */
export const transformRecommendedProductToProduct = (
  {
    code,
    images,
    links,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  }: ServiceRecommendedProduct,
  { currency }: { currency: CurrencyCode },
): Product => {
  const preSalePrice = preSalePrices?.find((preSale) => preSale.currency === price.currency);
  const [image] = images;

  return {
    /* Derived, not a constant. The request filters on the Vertex `availability`
       attribute rather than on `purchasable`, so nothing guarantees the two
       agree, and the card this replaces was given `purchasable` directly. */
    availability: purchasable ? 'in_stock' : 'out_of_stock',
    code: String(code),
    currencyCode: currency,
    /* The service returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: image ? [{ alt: '', type: 'image', url: getProductImageURL(image.href, 'normal') }] : [],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are
    // optional and unrendered, so they are left out until something needs them.
    partner: { name: partner.name },
    /* The single `price` field, not a lookup in `prices`. The request sets the
       currency, so this one is already quoted in it, which is what the adapter
       this replaces relied on. */
    price: price.amount,
    title,
    /* `rel`, not `href`. The recommendations response puts the site-relative
       product path in `rel` and an absolute URL in `href` that points at a QA
       host outside production. */
    url: links[0]?.rel || '',
  };
};

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 *
 * `priority` is deliberately unset. The carousel is client-fetched behind
 * `LazyRender`, so it is never above the fold and its images should not pre-empt
 * ones that are.
 *
 * The badge follows `toProductCardBadge`, except that a single-partner rail skips
 * Top Brand. It describes the partner rather than the product, so on "Our bestsellers"
 * it would be on every card or none, directly below a `PartnerMaxiModule` that
 * already says Top Brand. The partner page's own bestsellers rail in
 * browse-productlistings suppresses it for the same reason.
 *
 * `isSinglePartnerRail` is required rather than defaulted, so a rail added later
 * has to say which kind it is rather than quietly badging.
 *
 * `delivery` is the option the API resolved, and nothing about it is derived here.
 * That is the point rather than an implementation detail. The endpoint chooses one
 * real option and returns both halves or neither, so a card's date and its price
 * provably describe the same option. An absent `delivery` means no delivery line,
 * not a reason to fall back on `attributes.delivery_days` and
 * `attributes.delivery_cost`: those describe two different options, and pairing
 * them put the arrival 1 to 4 days earlier than the PLP grid's own card for the
 * same product on 74% of products, promising a date no option achieves. That
 * measurement is why deriving one here was removed rather than shipped, and it is
 * kept in `IRecommendationsAttributes` because it is the obvious implementation
 * and someone will reach for it again.
 *
 * Most cards will carry no line for now. Only STANDARD and FREE are retrievable
 * from the Vertex catalogue until DISC-4527 lands, so most products resolve
 * nothing, exactly as on the PLP grid. Matching the grid is the requirement here,
 * sparse coverage included.
 *
 * There is no delivery zone check either. The request names the zone, so a product
 * carrying an option is one the API has already decided we can quote for that
 * customer, and re-checking here would be a second opinion formed from less
 * information than the server has.
 */
export const transformRecommendedProductToProductCarouselItem = (
  product: ServiceRecommendedProduct,
  { currency, isSinglePartnerRail, now = new Date(), promoteAsSale }: RecommendationsCardOptions,
): ProductCarouselItem => {
  /* The endpoint passes the catalogue's attribute map through untouched, so the two
     flags are read by name before the shared rule ranks them. A suppressed rule does
     not take the slot with it, so a Most Loved product on the same-seller rail keeps
     its badge. */
  const badge = toProductCardBadge(
    {
      highlyRated: isHighlyRated(product.attributes),
      starBrand: isTopBrand(product.attributes),
    },
    { suppressPartnerBadges: isSinglePartnerRail },
  );
  const rating = toProductCardRating(product.ratings?.averageRating);
  const delivery = product.delivery ? toProductCardDelivery(product.delivery, now) : undefined;

  return {
    ...(badge && { badge }),
    ...(delivery && { delivery }),
    /* The card ignores `isNew` once a rating is given, showing the rating in the
       slot the "NEW" label would occupy, so both are passed and the card chooses. */
    isNew: product.isNew,
    product: transformRecommendedProductToProduct(product, { currency }),
    /* From the site-wide Contentstack switch, not `product.onSale`. No discount in
       the catalogue distinguishes a sale from an offer, so presenting one as either
       is a decision about the whole site rather than about this product. */
    promoteAsSale,
    ...(rating !== undefined && { rating }),
  };
};
